import React, { useEffect, useLayoutEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectExpenseReportLoadingFromState,
  selectExpenseReportsFullPaginatedData,
  selectExpenseReportsListModels,
  selectGettingOutOfPocketExpensesEnabledFromState,
  selectisEmptyFromState,
} from 'src/app/SubmitterExpenseReports/redux/submitterExpenseReports.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import ExpenseReportsBox, {
  ExpenseReportsAnalyticsEventProperties,
  ExpenseReportsBoxInfiniteScrollProps,
  ExpenseReportsFiltersBody,
} from 'src/components/ExpenseReportsBox/ExpenseReportsBox'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import TableEmptyState, {
  TableEmptyStateDesigns,
} from 'src/components/TableEmptyState/TableEmptyState'
import { useBackgroundDispatch } from 'src/hooks'
import {
  ExpenseReportModel,
  ExpenseReportStatus,
} from 'src/models/expenseReport.model'
import { createSetIsDeleteExpenseReportsPopupOpen } from 'src/redux/actions/app.actions'
import {
  getSubmitterExpenseReports,
  getSubmitterExpenseReportsLength,
} from 'src/redux/actions/data/expenses.actions'
import { selectIsCreateDeleteExpenseReportsPopupOpenFromState } from 'src/redux/selectors/app.selectors'
import { selectOutOfPocketExpensesVisible } from 'src/redux/selectors/data/organizations.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

const SubmitterExpenseReports: React.FC<React.PropsWithChildren> = () => {
  const dispatch = useBackgroundDispatch()
  const expenseReportsFullPaginatedData = useSelector(
    selectExpenseReportsFullPaginatedData,
  )
  const gettingOutOfPocketExpensesEnabled = useSelector(
    selectGettingOutOfPocketExpensesEnabledFromState,
  )
  const expenseReports = useSelector(selectExpenseReportsListModels)
  const expenseReportsLoading = useSelector(selectExpenseReportLoadingFromState)
  const outOfPocketExpensesEnabled = useSelector(
    selectOutOfPocketExpensesVisible,
  )
  const isEmptyReports = useSelector(selectisEmptyFromState)
  const isDeleteExpenseReportPopupOpen = useSelector(
    selectIsCreateDeleteExpenseReportsPopupOpenFromState,
  )
  const [bulkSelected, setBulkSelected] = useState<boolean>(false)
  const [selectedExpenseReports, setSelectedExpenseReports] = useState<
    ExpenseReportModel[]
  >([])
  const appName = useSelector(selectAppName)
  const { analytics } = useAnalyticsContext()

  const [selectedFilters, setSelectedFilters] = useState<
    ExpenseReportsAnalyticsEventProperties | undefined
  >(undefined)

  const fetchExpenseReports = (
    filtersBody: ExpenseReportsFiltersBody,
    page: number | undefined,
  ): void => {
    dispatch(
      getSubmitterExpenseReports({
        page: page,
        ...filtersBody,
      }),
    )
  }

  useLayoutEffect((): void => {
    dispatch(getSubmitterExpenseReportsLength())
  }, [])

  const expenseReportsInfiniteScrollProps: ExpenseReportsBoxInfiniteScrollProps =
    {
      ...expenseReportsFullPaginatedData,
      fetch: fetchExpenseReports,
    }

  const handleExpenseReportsCheckboxClick = (
    option: ExpenseReportModel,
  ): void => {
    if (!selectedExpenseReports.includes(option)) {
      if (selectedExpenseReports.length + 1 === expenseReports.length) {
        setBulkSelected(true)
      }
      setSelectedExpenseReports((prev) => [...prev, option])
    } else {
      if (bulkSelected) {
        setBulkSelected(false)
      }
      setSelectedExpenseReports((prev) =>
        prev.filter((item) => item !== option),
      )
    }
  }

  const handleBulkExpenseCheckboxClick = (): void => {
    if (expenseReports !== undefined) {
      bulkSelected
        ? setSelectedExpenseReports([])
        : setSelectedExpenseReports(
            expenseReports
              .filter((item) => item.status === ExpenseReportStatus.Draft)
              .map((item) => item) || [],
          )
    }
    setBulkSelected(!bulkSelected)
  }

  const handleDeleteReports = (): void => {
    const expenseReportIds = selectedExpenseReports.map((item) => item.id)
    dispatch(createSetIsDeleteExpenseReportsPopupOpen(true, expenseReportIds))
  }

  const handleCloseToolbar = (): void => {
    setBulkSelected(false)
    setSelectedExpenseReports([])
  }

  useEffect((): void => {
    if (!isDeleteExpenseReportPopupOpen) {
      setBulkSelected(false)
      setSelectedExpenseReports([])
    }
  }, [isDeleteExpenseReportPopupOpen])

  // analytics
  useEffect(() => {
    if (selectedFilters) {
      analytics?.track('your expense reports filter applied', {
        report_status: selectedFilters.report_status,
      })
    }
  }, [selectedFilters])

  return gettingOutOfPocketExpensesEnabled ? (
    <LoadingBackgroundPage />
  ) : outOfPocketExpensesEnabled ? (
    <>
      <Helmet>
        <title>{`Your Reports | ${appName}`}</title>
        <meta name="description" content={`Your Reports page of ${appName}`} />
      </Helmet>
      <BackgroundPageContent noStackingIndex>
        {isEmptyReports ? (
          expenseReportsLoading ? (
            <LoadingBackgroundPage />
          ) : (
            <TableEmptyState
              design={TableEmptyStateDesigns.SubmitterExpenseReports}
              wide
            />
          )
        ) : (
          <ExpenseReportsBox
            expenseReports={expenseReports}
            expenseReportsInfiniteScrollProps={
              expenseReportsInfiniteScrollProps
            }
            selectedExpenseReports={selectedExpenseReports}
            bulkSelected={bulkSelected}
            handleExpenseReportsCheckboxClick={
              handleExpenseReportsCheckboxClick
            }
            handleBulkExpenseReportsCheckboxClick={
              handleBulkExpenseCheckboxClick
            }
            handleReportBulkAction={handleDeleteReports}
            handleCloseToolbar={handleCloseToolbar}
            allReports={false}
            hasApproverPermissions={false}
            setSelectedFilters={setSelectedFilters}
            context={'submitter'}
          />
        )}
      </BackgroundPageContent>
    </>
  ) : (
    <NotFoundBackgroundPage />
  )
}

export default SubmitterExpenseReports
